import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, PinOff } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { http, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Panel, Info } from "@/components/admin/PageBits";
import { ScorePanel } from "@/components/admin/ScorePanel";
import { AppointmentPanel } from "@/components/admin/AppointmentPanel";
import { NotesPanel, HistoryPanel } from "@/components/admin/NotesHistory";
import { DeleteRequestButton } from "@/components/admin/DeleteRequestButton";
import { RedeemPanel } from "@/components/admin/RedeemPanel";
import { ActionsPanel } from "@/components/admin/ActionsPanel";
import { DocumentReviewPanel } from "@/components/admin/DocumentReviewPanel";

export const RequestDetailSheet = ({ requestId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [pinned, setPinned] = useState(() => localStorage.getItem("rw_sheet_pinned") === "1");
  useEffect(() => { localStorage.setItem("rw_sheet_pinned", pinned ? "1" : "0"); }, [pinned]);
  const keepOpen = (e) => pinned && e.preventDefault();

  const load = useCallback(async () => {
    if (!requestId) return;
    try {
      const { data } = await http.get(`/admin/requests/${requestId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [requestId]);

  useEffect(() => { setDetail(null); load(); }, [load]);

  useRealtimeEvent((m) => {
    if (!requestId) return;
    const id = m.data?.request_id || m.data?.id;
    if (m.event === "request.deleted" && m.data.id === requestId) onClose();
    else if (id === requestId || m.event === "resync") load();
  });

  const req = detail?.request;
  return (
    <Sheet open={!!requestId} modal={!pinned} onOpenChange={(o) => !o && onClose()}>
      <SheetContent hideOverlay={pinned} onInteractOutside={keepOpen} onEscapeKeyDown={keepOpen} className={`w-full overflow-y-auto border-white/10 bg-[#0B111E] text-slate-200 sm:max-w-xl ${pinned ? "top-14 h-[calc(100%-3.5rem)] shadow-[-20px_0_40px_rgba(0,0,0,0.5)]" : ""}`} data-testid="request-detail-sheet">
        <button onClick={() => setPinned(!pinned)} className={`absolute right-12 top-3 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors ${pinned ? "border-[var(--brand)] text-[var(--brand)]" : "border-white/10 text-slate-400 hover:text-slate-200"}`} data-testid="detail-pin-btn" aria-pressed={pinned}>
          {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />} {pinned ? "Desafixar" : "Fixar"}
        </button>
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl text-slate-50">Detalhes da solicitação</SheetTitle>
          <SheetDescription className="font-mono-x text-xs text-slate-500" data-testid="detail-protocol">{req?.protocolo || "Carregando…"}</SheetDescription>
        </SheetHeader>
        {req && (
          <div className="mt-6 space-y-4">
            <Panel title="Identificação">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Info label="Razão social" value={req.company?.razao_social || (req.company_lookup === "PENDENTE" ? "Identificando…" : "Não identificada")} testId="detail-razao-social" /></div>
                <div className="col-span-2"><Info label="Nome fantasia" value={req.company?.nome_fantasia || "—"} testId="detail-nome-fantasia" /></div>
                <Info label="Tipo de cliente" value={req.tipo === "PF" ? "Pessoa Física (PF)" : "Pessoa Jurídica (PJ)"} testId="detail-tipo" />
                <Info label={req.tipo === "PF" ? "CPF" : "CNPJ"} value={req.cnpj_fmt} mono testId="detail-cnpj" />
                <Info label="Entrada" value={formatDateTime(req.created_at)} />
                <Info label="Identificador" value={req.id.slice(0, 8)} mono />
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Status</p><div className="mt-1"><StatusBadge status={req.status} testId="detail-status" /></div></div>
              </div>
            </Panel>
            {req.status === "VALIDANDO_DOCUMENTO" && <DocumentReviewPanel req={req} onChanged={load} />}
            {["RESGATE_SOLICITADO", "RESGATE_CONCLUIDO"].includes(req.status) && <RedeemPanel req={req} onChanged={load} />}
            <ActionsPanel req={req} phoneHint={detail.appointment?.telefone} onChanged={load} />
            <ScorePanel req={req} scores={detail.scores} onChanged={load} />
            {detail.appointment && <AppointmentPanel appt={detail.appointment} onChanged={load} />}
            <NotesPanel requestId={req.id} notes={detail.notes} onChanged={load} />
            <HistoryPanel history={detail.history} />
            <DeleteRequestButton requestId={req.id} onDeleted={onClose} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
